<template>
  <v-container fluid>
    <PageHeader title="Whales">
      <template #icon>
        <humpback-icon class="mr-4 scale-tall" />
      </template>
      <template #actions>
        <v-btn
          v-if="hasScope('whales:write')"
          color="primary"
          @click="createWhale"
        >
          Add New Whale Profile
        </v-btn>
      </template>
    </PageHeader>
    <v-row>
      <v-col>
        <v-text-field
          v-model="query"
          prepend-icon="mdi-magnify"
          label="Search"
          density="compact"
          single-line
          hide-details
          clearable
        />
      </v-col>
      <v-col>
        <v-select
          v-model="selectedSpecies"
          :items="enums.species"
          label="Species"
          name="species"
          density="compact"
          multiple
          clearable
          @keyup="executeSearch"
        >
          <template #selection="{ item, index }">
            <span v-if="index < maxSelectDisplay">{{ item }} &nbsp;</span>
            <span
              v-if="index === maxSelectDisplay"
              class="grey-text text-caption"
            >(+{{ selectedSpecies.length - maxSelectDisplay }} more)</span>
          </template>
          <template #prepend>
            <humpback-icon />
          </template>
        </v-select>
      </v-col>
    </v-row>
    <v-row v-if="canWrite && unregisteredWhales.length > 0">
      <v-col cols="12">
        <v-alert
          type="warning"
          variant="tonal"
        >
          <div class="d-flex align-center">
            <span>
              {{ unregisteredWhales.length }} whale ID(s) appear in sightings but have no whale profile.
            </span>
            <v-spacer />
            <v-btn
              size="small"
              variant="text"
              @click="showUnregistered = !showUnregistered"
            >
              {{ showUnregistered ? "Hide" : "Show" }}
            </v-btn>
          </div>
          <v-data-table
            v-if="showUnregistered"
            :headers="unregisteredHeaders"
            :items="unregisteredWhales"
            density="compact"
            class="mt-2"
          >
            <template #[`item.actions`]="{ item }">
              <v-btn
                size="small"
                color="primary"
                @click="createFromUnregistered(item)"
              >
                <v-icon>mdi-plus</v-icon>
                Create Profile
              </v-btn>
            </template>
          </v-data-table>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet
          elevation="2"
          class="pa-md-2"
        >
          <v-tabs
            v-model="archivedTab"
            color="primary"
            class="mb-2"
          >
            <v-tab :value="false">
              Active
            </v-tab>
            <v-tab :value="true">
              Archived
            </v-tab>
          </v-tabs>
          <v-data-table-server
            v-model:options="options"
            :headers="tableHeaders"
            :items="items"
            :loading="loading"
            :items-length="total"
            :row-props="rowProps"
            @click:row="handleRowClick"
          >
            <template #[`item.thumbnail`]="{ item }">
              <v-img
                v-if="item.featurePhoto && item.featurePhoto.thumbnailUrl"
                :src="resolveStorageUrl(item.featurePhoto.thumbnailUrl)"
                width="56"
                height="56"
                cover
                class="my-1"
              />
              <div
                v-else
                class="thumbnail-placeholder my-1"
              >
                <v-icon size="small">
                  mdi-whale
                </v-icon>
              </div>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-icon
                    size="small"
                    v-bind="props"
                    v-on="props"
                    @click="openDrawer(item)"
                  >
                    mdi-arrow-expand
                  </v-icon>
                </template>
                <span>View Whale Details</span>
              </v-tooltip>
            </template>
          </v-data-table-server>
        </v-sheet>
      </v-col>
    </v-row>
    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      width="400"
    >
      <DrawerCard>
        <v-row
          v-if="selectedWhale && selectedWhale.id"
          class="pt-4"
        >
          <v-col>
            <v-row>
              <v-col cols="12">
                <photo-carousel :images="whaleImages" />
              </v-col>
            </v-row>
            <h3>{{ selectedWhale.whaleName }} ({{ selectedWhale.whaleId }})</h3>
            <v-text-field
              v-model="selectedWhale.species"
              label="Species"
              readonly
              variant="outlined"
              density="compact"
              hide-details
              class="mt-3"
            />
            <v-text-field
              v-model="selectedWhale.birthYear"
              label="Birth Year"
              readonly
              variant="outlined"
              density="compact"
              hide-details
              class="mt-3"
            />
            <v-text-field
              v-model="selectedWhale.behaviours"
              label="Behaviours"
              readonly
              variant="outlined"
              density="compact"
              hide-details
              class="mt-3"
            />
            <v-text-field
              v-model="selectedWhale.features"
              label="Features"
              readonly
              variant="outlined"
              density="compact"
              hide-details
              class="mt-3"
            />
          </v-col>
        </v-row>
        <template #actions>
          <v-card-actions v-if="hasScope('whales:write')">
            <v-btn
              color="primary"
              size="small"
              @click="viewWhaleDetails(selectedWhale)"
            >
              <v-icon>mdi-eye</v-icon>
              View Profile
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="!archived"
              color="warning"
              size="small"
              @click="openArchive(selectedWhale)"
            >
              <v-icon>mdi-archive-arrow-down</v-icon>
              Archive
            </v-btn>
            <v-btn
              v-else
              color="success"
              size="small"
              @click="openRestore(selectedWhale)"
            >
              <v-icon>mdi-archive-arrow-up</v-icon>
              Restore
            </v-btn>
            <v-btn
              color="error"
              size="small"
              :disabled="deleteDialog"
              @click="deleteItem(selectedWhale)"
            >
              <v-icon>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
          <v-card-actions v-else>
            <v-spacer />
            <v-btn
              color="primary"
              @click="viewWhaleDetails(selectedWhale)"
            >
              <v-icon>mdi-eye</v-icon>
              View Profile
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </template>
      </DrawerCard>
    </v-navigation-drawer>
    <whale-details />
    <whale-quick-view />
    <whale-rich-view />
    <delete
      :id="selectedItem.id"
      :name="selectedItem.whaleName"
      :delete-action="handleDelete"
    />
    <archive
      :id="selectedItem.id"
      :name="selectedItem.whaleName"
      :action="setWhaleArchived"
      :dialog="archiveDialog"
      :close="closeArchive"
      @update:dialog="archiveDialog = $event"
    />
    <restore
      :id="selectedItem.id"
      :name="selectedItem.whaleName"
      :action="setWhaleArchived"
      :dialog="restoreDialog"
      :close="closeRestore"
      @update:dialog="restoreDialog = $event"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DrawerCard from "@/components/DrawerCard";
import PageHeader from "@/components/PageHeader";
import { debounce } from "lodash";
import WhaleDetails from "@/components/whale/Details.vue";
import WhaleQuickView from "@/components/whale/WhaleQuickView.vue";
import WhaleRichView from "@/components/whale/WhaleRichView.vue";
import enums from "@/helpers/enums";
import HumpbackIcon from "@/components/svgs/HumpbackIcon.vue";
import Delete from "@/components/Delete.vue";
import Archive from "@/components/Archive.vue";
import Restore from "@/components/Restore.vue";
import PhotoCarousel from "@/components/PhotoCarousel.vue";
import whalesApi from "@/services/api/whales";

export default {
  components: {
    DrawerCard,
    PageHeader,
    WhaleDetails,
    WhaleQuickView,
    WhaleRichView,
    HumpbackIcon,
    Delete,
    Archive,
    Restore,
    PhotoCarousel,
  },

  /**
   *
   * clan: Optional[str]
   *     pod: Optional[str]
   *     matriline: Optional[str]
   *     birth_year: Optional[int]
   */
  data() {
    return {
      maxSelectDisplay: 1,
      visible: true,
      query: "",
      dateTimeFullText: null,
      dateField: false,
      selectedWhale: {},
      selectedItem: {},
      selectedSpecies: [],
      options: {},
      drawerOpen: false,
      archiveDialog: false,
      restoreDialog: false,
      whaleImages: [],
      showUnregistered: false,
      unregisteredHeaders: [
        { title: "Whale Id", sortable: true, key: "whaleId" },
        { title: "Name", sortable: true, key: "whaleName" },
        { title: "Species", sortable: true, key: "species" },
        { title: "Sightings", sortable: true, key: "sightingCount" },
        { title: "", sortable: false, key: "actions", align: "end" },
      ],
      fullHeaders: [
        { title: "", sortable: false, key: "thumbnail", width: "72px" },
        { title: "Whale Id", sortable: true, key: "whaleId" },
        { title: "Name", sortable: true, key: "whaleName" },
        { title: "Species", sortable: true, key: "species" },
        { title: "Clan", sortable: true, key: "clan" },
        { title: "Pod", sortable: true, key: "pod" },
        {
          title: "Actions",
          sortable: false,
          key: "actions",
          align: "center",
          width: "10%",
        },
      ],
      compactHeaders: [
        { title: "", sortable: false, key: "thumbnail", width: "72px" },
        { title: "Whale Id", sortable: true, key: "whaleId" },
        { title: "Name", sortable: true, key: "whaleName" },
        {
          title: "Actions",
          sortable: false,
          key: "actions",
          align: "center",
          width: "10%",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      loading: "whales/loading",
      items: "whales/items",
      searchOptions: "whales/searchOptions",
      searchQuery: "whales/searchQuery",
      total: "whales/total",
      whaleDialog: "whale/details/dialog",
      deleteDialog: "delete/dialog",
      hasScope: "auth/hasScope",
      unregisteredWhales: "whales/unregisteredWhales",
      archived: "whales/archived",
    }),
    canWrite() {
      return this.hasScope("whales:write");
    },
    enums() {
      return enums;
    },
    tableHeaders() {
      return this.drawerOpen ? this.compactHeaders : this.fullHeaders;
    },
    archivedTab: {
      get() {
        return this.archived;
      },
      set(value) {
        this.setArchived(value);
        this.drawerOpen = false;
        this.executeSearch();
      },
    },
  },
  watch: {
    query() {
      if (this.query == null) this.query = "";
      this.debouncedSearch();
      this.options.page = 1;
    },
    async selectedSpecies() {
      await this.executeSearch()
    },
    options() {
      this.executeSearch();
    },
  },
  async mounted() {
    await this.reload();
    if (this.canWrite) {
      await this.loadUnregisteredWhales();
    }
  },
  created() {
    this.options = this.searchOptions;
    this.query = this.searchQuery;
    if (this.searchSpecies != null) {
      this.selectedSpecies = this.searchSpecies;
    }
    this.debouncedSearch = debounce(this.executeSearch, 250);
    this.close()
  },
  methods: {
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    ...mapActions({
      search: "whales/search",
      open: "whale/details/open",
      reload: "whales/reload",
      close: "whale/details/close",
      openDelete: "delete/open",
      deleteWhale: "whales/deleteWhale",
      openRichView: "whale/view/openRich",
      loadUnregisteredWhales: "whales/loadUnregisteredWhales",
      setArchived: "whales/setArchived",
      setWhaleArchived: "whales/setWhaleArchived",
    }),
    async executeSearch() {
      await this.search({
        ...this.options,
        query: this.query,
        species: this.selectedSpecies,
      });
    },
    async openDrawer(item) {
      this.selectedWhale = { ...item };
      this.drawerOpen = true;
      this.whaleImages = [];
      if (item.whaleId) {
        const result = await whalesApi.imagesForWhale(item.whaleId);
        const featurePhotoId = item.featurePhotoId;
        if (featurePhotoId) {
          const feature = result.data.find((img) => img.id === featurePhotoId);
          const rest = result.data.filter((img) => img.id !== featurePhotoId);
          this.whaleImages = feature ? [feature, ...rest] : result.data;
        } else {
          this.whaleImages = result.data;
        }
      }
    },
    rowProps(data) {
      const item = data.item;
      if (item && this.selectedWhale && item.id === this.selectedWhale.id) {
        return { class: "selected-row" };
      }
      return {};
    },
    handleRowClick(e, rowObject) {
      this.openDrawer(rowObject.item);
    },
    editItem(item) {
      this.open({ whale: item });
    },
    viewWhaleDetails(item) {
      this.openRichView({ whale: item });
    },
    deleteItem(item) {
      this.selectedItem = item;
      this.openDelete();
    },
    openArchive(item) {
      this.selectedItem = item;
      this.archiveDialog = true;
    },
    openRestore(item) {
      this.selectedItem = item;
      this.restoreDialog = true;
    },
    closeArchive() {
      this.archiveDialog = false;
      this.drawerOpen = false;
      this.executeSearch();
    },
    closeRestore() {
      this.restoreDialog = false;
      this.drawerOpen = false;
      this.executeSearch();
    },
    async handleDelete() {
      await this.deleteWhale(this.selectedItem.id);
      this.drawerOpen = false;
    },
    async createWhale() {
      await this.open({ whale: null });
    },
    createFromUnregistered(item) {
      this.open({
        whale: {
          whaleId: item.whaleId,
          whaleName: item.whaleName,
          species: item.species,
          ecotype: item.ecotype,
          clan: item.clan,
          pod: item.pod,
        },
      });
    }
  },
};
</script>

<style scoped>
.thumbnail-placeholder {
  width: 56px;
  height: 56px;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}



.scale-tall {
  height: 100%;
}
.center-vertically {
  align-items: center;
  display: flex;
}

:deep(.v-list__tile) {
  height: 1rem;
}

:deep(tr.selected-row) {
  background-color: #79d9f4;
}

</style>
